import { type CountryCode } from "@/config/country-config";
import { getCountryRules } from "@/config/country-rules";
import type { LoanCustomerForm } from "@/features/customer-information/hooks/use-loan-customer-form";
import {
	buildFinnishIban,
	resolveSwedishAccountNumberForSubmit,
} from "@/features/customer-information/utils";
import { LoanCustomerInformationPayload } from "../types";

const CUSTOMER_INFO_TEST_EMAIL = "testtestesen+skip-credit-decision@test.com";

export const mapLoanCustomerFormToPayload = ({
	values,
	isCoapplicant,
	purpose,
	country,
	hasMultipleTrustedAccounts,
	mock = false,
}: {
	values: LoanCustomerForm;
	isCoapplicant: boolean;
	purpose: string | undefined;
	country: CountryCode;
	hasMultipleTrustedAccounts: boolean;
	/** `useRuntimeConfig().mock`: the mock skips the credit decision by email. */
	mock?: boolean;
}): LoanCustomerInformationPayload => {
	const { payoutAccount, fixedCitizenship, dueDateChoice } =
		getCountryRules(country);
	const accountNumberForSubmit = (() => {
		if (isCoapplicant) return "";
		if (payoutAccount === "finnish-iban") {
			return buildFinnishIban(values.accountNumber);
		}
		return resolveSwedishAccountNumberForSubmit(
			values,
			values.isWholeAccountNumber || hasMultipleTrustedAccounts
		);
	})();

	const {
		consent: _consent,
		clearingNumber: _clearingNumber,
		isWholeAccountNumber: _isWholeAccountNumber,
		prefilledAccountNumber: _prefilledAccountNumber,
		loanPurpose: _loanPurpose,
		dueDate,
		numberOfCars,
		...body
	} = values;

	void _consent;
	void _clearingNumber;
	void _isWholeAccountNumber;
	void _prefilledAccountNumber;
	void _loanPurpose;

	return {
		...body,
		accountNumber: accountNumberForSubmit,
		...(mock ? { email: CUSTOMER_INFO_TEST_EMAIL } : {}),
		...(fixedCitizenship ? { citizenship: [...fixedCitizenship] } : {}),
		...(purpose ? { purpose } : {}),
		...(dueDateChoice && dueDate ? { dueDate: Number(dueDate) } : {}),
		...(numberOfCars !== null ? { numberOfCars } : {}),
	};
};
